// perfect-scrollbar's rail is absolutely positioned and always spans one viewport
// height anchored at the current scrollTop, so the container's own `scrollHeight`
// can never read as less than `scrollTop + clientHeight` — unreliable for measuring
// real content height. It also never recalculates on its own when content resizes
// (no MutationObserver/ResizeObserver), only on its native 'scroll' handler. So
// after content changes size, we measure the real content directly and move
// `scrollTop` back within its bounds ourselves (which triggers that handler).
function getScrollMax(container: HTMLElement): number {
  const content = Array.from(container.children).find(
    (child) =>
      !child.classList.contains('ps__rail-x') &&
      !child.classList.contains('ps__rail-y')
  );

  return Math.max(
    0,
    (content?.getBoundingClientRect().height ?? 0) - container.clientHeight
  );
}

function findPerfectScrollbarContainer(
  node: HTMLElement | null
): HTMLElement | null {
  let current: HTMLElement | null = node;

  while (current && !current.classList.contains('ps')) {
    current = current.parentElement;
  }

  return current;
}

/** Call after removing content under a ScrollbarComponent, so a stale scroll
 * position doesn't leave blank space below the now-shorter content. */
export function updateNearestScrollbar(node: HTMLElement | null): void {
  const container = findPerfectScrollbarContainer(node);
  if (!container) {
    return;
  }

  const maxScroll = getScrollMax(container);
  if (container.scrollTop > maxScroll) {
    container.scrollTop = maxScroll;
  }
}

/** Scrolls the nearest perfect-scrollbar ancestor to the bottom of its real
 * content. Not via `scrollIntoView`/`scrollTo(..., {behavior:'smooth'})`: Chrome
 * silently no-ops both on `overflow: hidden` containers (which is what
 * perfect-scrollbar uses), so this jump is instant, not animated. Falls back to
 * native scrollIntoView if no perfect-scrollbar ancestor is found. */
export function scrollNearestScrollbarToBottom(node: HTMLElement | null): void {
  const container = findPerfectScrollbarContainer(node);

  if (!container) {
    node?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    return;
  }

  container.scrollTop = getScrollMax(container);
}
